import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../../services/auth';
import { api } from '../../../services/api';
import {
  ConversationSession,
  ConversationTimelineItem,
  ProgressBlockItem,
  ClarificationRequestItem,
  UserInputItem,
  ReportItem,
  FollowupQuestionItem,
  FollowupResponseItem,
  ProgressStep,
  AnalysisPhase,
  generateTimelineId,
} from '../../../types/conversation';
import { ClarificationQuestion, ClarificationResponse } from '../../ClarificationMessage';
import { TEST_SESSION, TEST_TRIGGER } from '../testData';
import { mapFullSessionToConversationSession } from '../utils/sessionMapper';

const STORAGE_KEY_PREFIX = 'deep-analysis-session-';
const TOKENS_LIMIT = 50000;
const IS_DEV = import.meta.env.DEV;

interface UseConversationSessionOptions {
  onError?: (error: string) => void;
}

interface UseConversationSessionReturn {
  session: ConversationSession;
  startAnalysis: (document: string, meta?: { personalDataAcknowledged?: boolean }) => Promise<void>;
  submitClarification: (responses: ClarificationResponse[]) => Promise<void>;
  sendFollowup: (message: string) => Promise<void>;
  reset: () => void;
  cancelClarification: () => void;
  loadSession: (sessionId: string) => Promise<void>;
  isLoadingSession: boolean;
  loadError: string | null;
}

export function useConversationSession(
  options: UseConversationSessionOptions = {}
): UseConversationSessionReturn {
  const { onError } = options;
  const { t } = useTranslation();

  const [session, setSession] = useState<ConversationSession>(() => ({
    id: null,
    status: 'idle',
    timeline: [],
    currentRound: 0,
    tokensUsed: 0,
    tokensLimit: TOKENS_LIMIT,
    error: null,
  }));

  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Persist session to sessionStorage
  useEffect(() => {
    if (session.id) {
      try {
        sessionStorage.setItem(
          `${STORAGE_KEY_PREFIX}${session.id}`,
          JSON.stringify(session)
        );
      } catch (e) {
        console.warn('Failed to persist session:', e);
      }
    }
  }, [session]);

  // Helper: Add item to timeline (kept for future use)
  const _addTimelineItem = useCallback((item: ConversationTimelineItem) => {
    setSession(prev => ({
      ...prev,
      timeline: [...prev.timeline, item],
    }));
  }, []);
  // Suppress unused warning
  void _addTimelineItem;

  // Helper: Update last item of a specific type
  const updateLastItemOfType = useCallback(
    <T extends ConversationTimelineItem>(
      type: T['type'],
      updater: (item: T) => T
    ) => {
      setSession(prev => {
        const timeline = [...prev.timeline];
        // Find last item of this type
        for (let i = timeline.length - 1; i >= 0; i--) {
          if (timeline[i].type === type) {
            timeline[i] = updater(timeline[i] as T);
            break;
          }
        }
        return { ...prev, timeline };
      });
    },
    []
  );

  // Helper: Get or create current progress block
  const getOrCreateProgressBlock = useCallback(
    (phase: AnalysisPhase, roundNumber: number) => {
      setSession(prev => {
        const timeline = [...prev.timeline];
        const lastItem = timeline[timeline.length - 1];

        // If last item is a streaming progress block, use it
        if (
          lastItem &&
          lastItem.type === 'progress_block' &&
          (lastItem as ProgressBlockItem).isStreaming
        ) {
          return prev;
        }

        // Create new progress block
        const newBlock: ProgressBlockItem = {
          id: generateTimelineId(),
          type: 'progress_block',
          createdAt: new Date().toISOString(),
          roundNumber,
          steps: [],
          phase,
          isComplete: false,
          isStreaming: true,
        };

        return {
          ...prev,
          timeline: [...timeline, newBlock],
        };
      });
    },
    []
  );

  // Helper: Add step to current progress block
  const addProgressStep = useCallback((step: ProgressStep, phase?: AnalysisPhase) => {
    setSession(prev => {
      const timeline = [...prev.timeline];

      // Find the last progress block that is streaming
      for (let i = timeline.length - 1; i >= 0; i--) {
        if (timeline[i].type === 'progress_block') {
          const block = timeline[i] as ProgressBlockItem;
          if (block.isStreaming) {
            // Mark previous step as completed
            const steps = [...block.steps];
            if (steps.length > 0) {
              steps[steps.length - 1] = { ...steps[steps.length - 1], completed: true };
            }
            steps.push(step);

            timeline[i] = {
              ...block,
              steps,
              phase: phase || block.phase,
            };
            break;
          }
        }
      }

      return { ...prev, timeline };
    });
  }, []);

  // Helper: Complete current progress block
  const completeProgressBlock = useCallback(() => {
    setSession(prev => {
      const timeline = [...prev.timeline];

      for (let i = timeline.length - 1; i >= 0; i--) {
        if (timeline[i].type === 'progress_block') {
          const block = timeline[i] as ProgressBlockItem;
          if (block.isStreaming) {
            // Mark all steps as completed
            const steps = block.steps.map(s => ({ ...s, completed: true }));
            timeline[i] = {
              ...block,
              steps,
              isComplete: true,
              isStreaming: false,
            };
            break;
          }
        }
      }

      return { ...prev, timeline };
    });
  }, []);

  // Handle SSE events for deep analysis
  const handleDeepSSEEvent = useCallback(
    (eventType: string, data: Record<string, unknown>) => {
      // Handle clarification request
      if (eventType === 'clarification' || eventType === 'awaiting_clarification') {
        completeProgressBlock();

        const clarificationItem: ClarificationRequestItem = {
          id: generateTimelineId(),
          type: 'clarification_request',
          createdAt: new Date().toISOString(),
          roundNumber: session.currentRound,
          requestId: data.requestId as string,
          questions: data.questions as ClarificationQuestion[],
          status: 'pending',
        };

        setSession(prev => {
          // Check if we already have a pending clarification request to avoid duplicates
          const hasPendingClarification = prev.timeline.some(
            item => item.type === 'clarification_request' &&
                    (item as ClarificationRequestItem).status === 'pending'
          );

          if (hasPendingClarification) {
            // Just update the session status and id, don't add duplicate
            return {
              ...prev,
              id: (data.sessionId as string) || prev.id,
              status: 'awaiting_clarification',
            };
          }

          return {
            ...prev,
            id: (data.sessionId as string) || prev.id,
            status: 'awaiting_clarification',
            timeline: [...prev.timeline, clarificationItem],
          };
        });
        return;
      }

      // Handle progress events
      if (eventType === 'progress') {
        const message = data.message as string;
        const phase = data.phase as AnalysisPhase | undefined;

        if (message) {
          addProgressStep(
            {
              type: data.type as string || 'progress',
              message,
              timestamp: new Date().toISOString(),
              completed: false,
            },
            phase
          );
        }
      }

      // Handle completion
      if (eventType === 'complete') {
        completeProgressBlock();

        const reportData = data.report as Record<string, unknown>;
        if (reportData) {
          const reportItem: ReportItem = {
            id: generateTimelineId(),
            type: 'report',
            createdAt: new Date().toISOString(),
            roundNumber: session.currentRound,
            markdown: (reportData.markdown as string) || '',
          };

          setSession(prev => ({
            ...prev,
            id: (data.sessionId as string) || prev.id,
            status: 'complete',
            timeline: [...prev.timeline, reportItem],
          }));
        }
      }

      // Handle errors
      if (eventType === 'error') {
        const errorMsg = (data.error as string) || t('errors.analysisError');
        setSession(prev => ({
          ...prev,
          status: 'error',
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [session.currentRound, completeProgressBlock, addProgressStep, onError, t]
  );

  // Parse SSE stream
  const parseSSEStream = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEventType = line.substring(6).trim();
            continue;
          }
          if (line.startsWith('data:')) {
            const data = line.substring(5).trim();
            try {
              const parsed = JSON.parse(data);
              handleDeepSSEEvent(currentEventType, parsed);
            } catch {
              // Ignore parse errors for partial data
            }
          }
        }
      }
    },
    [handleDeepSSEEvent]
  );

  // Start analysis
  const startAnalysis = useCallback(
    async (document: string, meta?: { personalDataAcknowledged?: boolean }) => {
      if (!document.trim()) {
        const errorMsg = 'Wprowadz tekst dokumentu lub pytanie do analizy';
        setSession(prev => ({ ...prev, error: errorMsg }));
        onError?.(errorMsg);
        return;
      }

      // DEV MODE: Load test data when user types "[test]"
      if (IS_DEV && document.trim() === TEST_TRIGGER) {
        setSession({
          ...TEST_SESSION,
          id: 'test-session-' + Date.now(),
        });
        return;
      }

      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Reset session and add user input
      const userInput: UserInputItem = {
        id: generateTimelineId(),
        type: 'user_input',
        createdAt: new Date().toISOString(),
        roundNumber: 1,
        content: document,
      };

      setSession({
        id: null,
        status: 'analyzing',
        timeline: [userInput],
        currentRound: 1,
        tokensUsed: 0,
        tokensLimit: TOKENS_LIMIT,
        error: null,
      });

      // Create initial progress block
      getOrCreateProgressBlock('clarification', 1);

      try {
        const accessToken = authAPI.getAccessToken();
        const response = await fetch('/api/v1/analysis/deep/start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            document,
            personalDataAcknowledged: meta?.personalDataAcknowledged ?? false,
            options: {
              maxJudgments: 30,
              searchType: 'semantic',
            },
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('errors.analysisError'));
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error(t('errors.cannotReadResponse'));
        }

        await parseSSEStream(reader);
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        const errorMsg = err instanceof Error ? err.message : t('errors.analysisError');
        setSession(prev => ({
          ...prev,
          status: 'error',
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [getOrCreateProgressBlock, parseSSEStream, onError, t]
  );

  // Submit clarification
  const submitClarification = useCallback(
    async (responses: ClarificationResponse[]) => {
      if (!session.id) return;

      // Update clarification item to show responses
      setSession(prev => {
        const timeline = [...prev.timeline];
        for (let i = timeline.length - 1; i >= 0; i--) {
          if (timeline[i].type === 'clarification_request') {
            const item = timeline[i] as ClarificationRequestItem;
            if (item.status === 'pending') {
              timeline[i] = {
                ...item,
                status: 'answered',
                responses,
              };
              break;
            }
          }
        }
        return {
          ...prev,
          status: 'analyzing',
          currentRound: prev.currentRound + 1,
          timeline,
        };
      });

      // Create new progress block for continuation
      getOrCreateProgressBlock('exploration', session.currentRound + 1);

      try {
        const accessToken = authAPI.getAccessToken();
        const response = await fetch(`/api/v1/analysis/deep/${session.id}/clarify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ responses }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('errors.continuationError'));
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error(t('errors.cannotReadResponse'));
        }

        await parseSSEStream(reader);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : t('errors.continuationError');
        setSession(prev => ({
          ...prev,
          status: 'error',
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [session.id, session.currentRound, getOrCreateProgressBlock, parseSSEStream, onError, t]
  );

  // Send followup
  const sendFollowup = useCallback(
    async (message: string) => {
      if (!session.id || !message.trim()) return;

      // Add followup question
      const followupQuestion: FollowupQuestionItem = {
        id: generateTimelineId(),
        type: 'followup_question',
        createdAt: new Date().toISOString(),
        roundNumber: session.currentRound,
        content: message,
      };

      // Add progress block for followup processing
      const progressBlockId = generateTimelineId();
      const progressBlock: ProgressBlockItem = {
        id: progressBlockId,
        type: 'progress_block',
        createdAt: new Date().toISOString(),
        roundNumber: session.currentRound,
        steps: [],
        phase: 'analysis',
        isComplete: false,
        isStreaming: true,
      };

      // Add streaming response placeholder (will be populated at the end)
      const responseId = generateTimelineId();
      const followupResponse: FollowupResponseItem = {
        id: responseId,
        type: 'followup_response',
        createdAt: new Date().toISOString(),
        roundNumber: session.currentRound,
        content: '',
        isStreaming: true,
      };

      setSession(prev => ({
        ...prev,
        timeline: [...prev.timeline, followupQuestion, progressBlock, followupResponse],
      }));

      // Helper to add step to the followup progress block
      const addFollowupProgressStep = (stepMessage: string) => {
        setSession(prev => {
          const timeline = [...prev.timeline];
          for (let i = timeline.length - 1; i >= 0; i--) {
            if (timeline[i].id === progressBlockId) {
              const block = timeline[i] as ProgressBlockItem;
              const steps = [...block.steps];
              // Mark previous step as completed
              if (steps.length > 0) {
                steps[steps.length - 1] = { ...steps[steps.length - 1], completed: true };
              }
              steps.push({
                type: 'progress',
                message: stepMessage,
                timestamp: new Date().toISOString(),
                completed: false,
              });
              timeline[i] = { ...block, steps };
              break;
            }
          }
          return { ...prev, timeline };
        });
      };

      // Helper to complete the progress block
      const completeFollowupProgressBlock = () => {
        setSession(prev => {
          const timeline = [...prev.timeline];
          for (let i = timeline.length - 1; i >= 0; i--) {
            if (timeline[i].id === progressBlockId) {
              const block = timeline[i] as ProgressBlockItem;
              const steps = block.steps.map(s => ({ ...s, completed: true }));
              timeline[i] = { ...block, steps, isComplete: true, isStreaming: false };
              break;
            }
          }
          return { ...prev, timeline };
        });
      };

      try {
        let token = authAPI.getAccessToken();

        const makeRequest = async (authToken: string | null) => {
          return fetch(`/api/v1/analysis/deep/${session.id}/followup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
            body: JSON.stringify({ message }),
          });
        };

        let response = await makeRequest(token);

        if (response.status === 401) {
          try {
            await authAPI.refreshToken();
            token = authAPI.getAccessToken();
            response = await makeRequest(token);
          } catch {
            throw new Error('Sesja wygasla. Zaloguj sie ponownie.');
          }
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Nie udalo sie wyslac wiadomosci');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event:')) continue;
            if (line.startsWith('data:')) {
              try {
                const data = JSON.parse(line.slice(5));

                if (data.type === 'thinking' || data.type === 'searching' || data.type === 'response_start') {
                  // Add to progress block only - don't update response item with status
                  if (data.message) {
                    addFollowupProgressStep(data.message);
                  }
                } else if (data.content) {
                  // Complete the progress block
                  completeFollowupProgressBlock();

                  // Update the response with final content
                  updateLastItemOfType<FollowupResponseItem>('followup_response', item => ({
                    ...item,
                    content: data.content,
                    referencedJudgments: data.referencedJudgments,
                    tokensUsed: data.tokensUsed,
                    isStreaming: false,
                  }));

                  if (data.tokensUsed) {
                    setSession(prev => ({
                      ...prev,
                      tokensUsed: prev.tokensUsed + data.tokensUsed,
                    }));
                  }
                }
              } catch {
                // Ignore parse errors
              }
            }
          }
        }

        // Make sure progress block is completed even if no explicit complete event
        completeFollowupProgressBlock();
      } catch (err) {
        const errorMsg = (err as Error).message;
        // Remove the progress block and streaming response on error
        setSession(prev => ({
          ...prev,
          timeline: prev.timeline.filter(item => item.id !== responseId && item.id !== progressBlockId),
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [session.id, session.currentRound, updateLastItemOfType, onError]
  );

  // Reset session
  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setSession({
      id: null,
      status: 'idle',
      timeline: [],
      currentRound: 0,
      tokensUsed: 0,
      tokensLimit: TOKENS_LIMIT,
      error: null,
    });
    setLoadError(null);
  }, []);

  // Cancel clarification (go back to idle)
  const cancelClarification = useCallback(() => {
    reset();
  }, [reset]);

  // Load a previous session
  const loadSession = useCallback(async (sessionId: string) => {
    setIsLoadingSession(true);
    setLoadError(null);

    try {
      const fullSession = await api.getDeepAnalysisSession(sessionId);
      const restoredSession = mapFullSessionToConversationSession(fullSession);
      setSession(restoredSession);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('errors.loadSession');
      setLoadError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsLoadingSession(false);
    }
  }, [onError, t]);

  return {
    session,
    startAnalysis,
    submitClarification,
    sendFollowup,
    reset,
    cancelClarification,
    loadSession,
    isLoadingSession,
    loadError,
  };
}

export default useConversationSession;
